import { useMediaQuery } from "@uidotdev/usehooks";
import { Loader2, type LucideIcon } from "lucide-react";
import {
	type ComponentProps,
	createContext,
	useContext,
	useEffect,
	useState,
} from "react";
import i18n from "@/lib/i18n";
import { cn } from "@/lib/utils";
import type { ResponsiveDialogMode } from "@/types/globals";
import { Button } from "../ui/button";
import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "../ui/dialog";
import {
	Drawer,
	DrawerClose,
	DrawerContent,
	DrawerDescription,
	DrawerFooter,
	DrawerHeader,
	DrawerTitle,
} from "../ui/drawer";
import { ScrollArea } from "../ui/scroll-area";

const DIALOG_MODE: ResponsiveDialogMode = "dialog";

const SetDismissibleContext = createContext<(dismissible: boolean) => void>(
	() => {},
);

// Keeps the surrounding dialog or drawer from closing by any route while `keepOpen` holds,
// e.g. while a form's submit is still running.
export const useKeepDialogOpen = (keepOpen: boolean) => {
	const setDismissible = useContext(SetDismissibleContext);

	useEffect(() => {
		setDismissible(!keepOpen);
		return () => setDismissible(true);
	}, [keepOpen, setDismissible]);
};

export interface ResponsiveDialogProps extends ComponentProps<"dialog"> {
	title: string;
	description: string;
	isOpen: boolean;
	dialogWidth?: string;
	onOpenChange?: (open: boolean) => void;
	forceMode?: ResponsiveDialogMode;
}

export const ResponsiveDialog = ({
	children,
	title,
	description,
	isOpen,
	onOpenChange,
	dialogWidth = "380px",
	forceMode,
}: ResponsiveDialogProps) => {
	const isDekstop = useMediaQuery("(min-width: 768px)");
	const [dismissible, setDismissible] = useState(true);
	const handleOpenChange = (open: boolean) => {
		if (open || dismissible) onOpenChange?.(open);
	};
	const content = (
		<SetDismissibleContext.Provider value={setDismissible}>
			{children}
		</SetDismissibleContext.Provider>
	);

	if (isDekstop || forceMode === "dialog") {
		return (
			<Dialog open={isOpen} onOpenChange={handleOpenChange} modal>
				<DialogContent
					onInteractOutside={(event) => {
						event.preventDefault();
					}}
					style={{ maxWidth: dialogWidth }}
				>
					<DialogHeader>
						<DialogTitle>{title}</DialogTitle>
						<DialogDescription className="text-pretty">
							{description}
						</DialogDescription>
					</DialogHeader>
					<ScrollArea className="max-h-[75dvh]">{content}</ScrollArea>
				</DialogContent>
			</Dialog>
		);
	}

	return (
		<Drawer
			modal
			open={isOpen}
			onOpenChange={handleOpenChange}
			dismissible={dismissible}
		>
			{/* DrawerContent is h-auto, so without a cap tall content grows past the top of
			    the viewport with no way to reach it — the header and footer stay put and
			    only the middle scrolls. */}
			<DrawerContent
				className="max-h-[92dvh]"
				onInteractOutside={(event) => {
					event.preventDefault();
				}}
			>
				<div className="flex flex-col w-full max-w-sm min-h-0 mx-auto">
					<DrawerHeader className="px-0 text-left shrink-0">
						<DrawerTitle>{title}</DrawerTitle>
						<DrawerDescription>{description}</DrawerDescription>
					</DrawerHeader>
					{/* The native scrollbar is hidden, not merely styled: it claims 15px from
					    this region, so every control inside ended up narrower than the Cancel
					    button in the footer below, which sits outside it. The desktop branch
					    hides it the same way, through ScrollArea. */}
					<div className="flex-1 min-h-0 px-1 -mx-1 overflow-y-auto overscroll-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
						{content}
					</div>
					<DrawerFooter className="px-0 pt-4 shrink-0">
						<DrawerClose asChild className="p-0">
							<Button variant="outline" className="shadow-none">
								{i18n.t("components.modal.cancel")}
							</Button>
						</DrawerClose>
					</DrawerFooter>
				</div>
			</DrawerContent>
		</Drawer>
	);
};

export interface ResponsiveDialogButton extends ComponentProps<"div"> {
	forceMode?: ResponsiveDialogMode;
	LoaderIcon?: LucideIcon;
	isLoading: boolean;
	disabled?: boolean;
	cancelButton?: boolean;
	cancelText?: string;
	loaderText?: string;
	submitText?: string;
	classSubmitButton?: string;
}

export const ResponsiveDialogButton = ({
	className,
	LoaderIcon = Loader2,
	forceMode = DIALOG_MODE,
	isLoading,
	disabled = false,
	cancelButton = true,
	cancelText = i18n.t("components.modal.cancel"),
	loaderText = `${i18n.t("components.modal.wait")}...`,
	submitText = i18n.t("components.modal.save"),
}: ResponsiveDialogButton) => {
	const isDekstop = useMediaQuery("(min-width: 768px)");

	return (
		<div
			className={cn(
				"w-full flex justify-end !mt-6 gap-2",
				forceMode === "drawer" ? "w-full" : "",
				className,
			)}
		>
			{forceMode === "dialog" && isDekstop && cancelButton && (
				<DialogClose asChild>
					<Button variant="ghost">{cancelText}</Button>
				</DialogClose>
			)}

			<Button
				type="submit"
				disabled={isLoading || disabled}
				className={cn(
					"w-full md:w-auto order-first md:order-last",
					forceMode === "drawer" ? "w-full md:w-full" : "",
				)}
			>
				{isLoading ? (
					<>
						<LoaderIcon className="animate-spin" />
						<span>{loaderText}</span>
					</>
				) : (
					<span>{submitText}</span>
				)}
			</Button>
		</div>
	);
};
